
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./VendorSupport.css";

const API_BASE = "https://bmtvendorvendorfinal.onrender.com/api/vendor/support";

const CATEGORIES = [
  "Account & Profile",
  "Bookings",
  "Payments",
  "Property",
  "Technical Issue",
  "Other",
];

const PRIORITIES = ["Low", "Medium", "High"];

const formatDate = (date) => {
  if (!date) return "—";
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "—";

  return parsed.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getStatusClass = (status = "Open") =>
  status.toLowerCase().replace(/\s+/g, "-");

export default function VendorSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");

  const [form, setForm] = useState({
    subject: "",
    category: "Bookings",
    priority: "Medium",
    description: "",
  });

  const request = useCallback(async (url, options = {}) => {
    const token = localStorage.getItem("vendorToken");

    const response = await fetch(url, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {}),
      },
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok || data.success === false) {
      throw new Error(
        response.status === 401
          ? "Session expired. Please login again."
          : data.message || "Request failed. Please try again."
      );
    }

    return data;
  }, []);

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const data = await request(API_BASE);
      setTickets(Array.isArray(data.tickets) ? data.tickets : []);
    } catch (err) {
      setError(err.message || "Unable to load support tickets.");
    } finally {
      setLoading(false);
    }
  }, [request]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const stats = useMemo(() => ({
    total: tickets.length,
    open: tickets.filter((ticket) =>
      ["open", "in progress"].includes(
        (ticket.status || "").toLowerCase()
      )
    ).length,
    resolved: tickets.filter((ticket) =>
      ["resolved", "closed"].includes(
        (ticket.status || "").toLowerCase()
      )
    ).length,
  }), [tickets]);

  const filteredTickets = useMemo(() => {
    const query = search.trim().toLowerCase();

    return tickets.filter((ticket) => {
      const searchable = [
        ticket.ticketNumber,
        ticket.subject,
        ticket.description,
        ticket.category,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));

      const matchesStatus =
        statusFilter === "All" || ticket.status === statusFilter;

      const matchesCategory =
        categoryFilter === "All" ||
        ticket.category === categoryFilter;

      return searchable && matchesStatus && matchesCategory;
    });
  }, [tickets, search, statusFilter, categoryFilter]);

  const updateForm = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const openCreatePage = () => {
    setError("");
    setSuccess("");
    setSelectedTicket(null);
    setShowCreateForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeCreatePage = () => {
    if (submitting) return;

    setShowCreateForm(false);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleCreateTicket = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");

    if (!form.subject.trim() || !form.description.trim()) {
      setError("Subject and description are required.");
      return;
    }

    try {
      setSubmitting(true);

      const data = await request(API_BASE, {
        method: "POST",
        body: JSON.stringify({
          subject: form.subject.trim(),
          category: form.category,
          priority: form.priority,
          description: form.description.trim(),
        }),
      });

      setForm({
        subject: "",
        category: "Bookings",
        priority: "Medium",
        description: "",
      });

      setShowCreateForm(false);
      setSuccess(data.message || "Support ticket created successfully.");

      await fetchTickets();
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.message || "Unable to create support ticket.");
    } finally {
      setSubmitting(false);
    }
  };

  const openTicketDetails = async (ticket) => {
    setSelectedTicket(ticket);
    setDetailLoading(true);
    setError("");

    try {
      const data = await request(`${API_BASE}/${ticket._id}`);
      if (data.ticket) setSelectedTicket(data.ticket);
    } catch (err) {
      setError(err.message || "Unable to load ticket details.");
    } finally {
      setDetailLoading(false);
    }
  };

  /* FULL PAGE CREATE FORM */
  if (showCreateForm) {
    return (
      <main className="vs-page vs-create-page">
        <div className="vs-container">
          <button
            type="button"
            className="vs-back-btn"
            onClick={closeCreatePage}
            disabled={submitting}
          >
            ← Back to Support
          </button>

          <header className="vs-create-header">
            <span className="vs-eyebrow">
              <span className="vs-eyebrow-dot" />
              VENDOR HELP CENTER
            </span>

            <h1>Create Support Ticket</h1>
            <p>
              Tell us about your issue. Our support team will review
              your request and respond as soon as possible.
            </p>
          </header>

          {error && (
            <div className="vs-alert vs-alert-error" role="alert">
              <span>!</span>
              <p>{error}</p>
              <button
                type="button"
                onClick={() => setError("")}
                aria-label="Dismiss error"
              >
                ×
              </button>
            </div>
          )}

          <section className="vs-create-card">
            <div className="vs-create-card-heading">
              <div>
                <h2>Ticket Information</h2>
                <p>Fields marked with * are required.</p>
              </div>
              <span className="vs-create-badge">NEW REQUEST</span>
            </div>

            <form onSubmit={handleCreateTicket}>
              <div className="vs-form-field">
                <label htmlFor="vs-subject">Subject *</label>
                <input
                  id="vs-subject"
                  name="subject"
                  value={form.subject}
                  onChange={updateForm}
                  placeholder="Briefly describe your issue"
                  maxLength={150}
                  required
                />
              </div>

              <div className="vs-form-grid">
                <div className="vs-form-field">
                  <label htmlFor="vs-category">Category *</label>
                  <select
                    id="vs-category"
                    name="category"
                    value={form.category}
                    onChange={updateForm}
                    required
                  >
                    {CATEGORIES.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="vs-form-field">
                  <label htmlFor="vs-priority">Priority *</label>
                  <select
                    id="vs-priority"
                    name="priority"
                    value={form.priority}
                    onChange={updateForm}
                    required
                  >
                    {PRIORITIES.map((priority) => (
                      <option key={priority} value={priority}>
                        {priority}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="vs-form-field">
                <label htmlFor="vs-description">
                  Describe your issue *
                </label>
                <textarea
                  id="vs-description"
                  name="description"
                  value={form.description}
                  onChange={updateForm}
                  placeholder="Explain what happened and include relevant details..."
                  rows={7}
                  maxLength={3000}
                  required
                />
                <span className="vs-character-count">
                  {form.description.length}/3000 characters
                </span>
              </div>

              <div className="vs-form-actions">
                <button
                  type="button"
                  className="vs-cancel-btn"
                  onClick={closeCreatePage}
                  disabled={submitting}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="vs-primary-btn"
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Submit Ticket →"}
                </button>
              </div>
            </form>
          </section>
        </div>
      </main>
    );
  }

  /* SUPPORT DASHBOARD */
  return (
    <main className="vs-page">
      <div className="vs-container">
        <header className="vs-header">
          <div>
            <div className="vs-eyebrow">
              <span className="vs-eyebrow-dot" />
              VENDOR HELP CENTER
            </div>
            <h1>Support Center</h1>
            <p>Raise a support ticket and track your requests in one place.</p>
          </div>

          <button
            className="vs-primary-btn"
            type="button"
            onClick={openCreatePage}
          >
            <span className="vs-plus">+</span>
            Create Support Ticket
          </button>
        </header>

        {error && (
          <div className="vs-alert vs-alert-error" role="alert">
            <span>!</span>
            <p>{error}</p>
            <button type="button" onClick={() => setError("")}>×</button>
          </div>
        )}

        {success && (
          <div className="vs-alert vs-alert-success" role="status">
            <span>✓</span>
            <p>{success}</p>
            <button type="button" onClick={() => setSuccess("")}>×</button>
          </div>
        )}

        <section className="vs-stats">
          <article className="vs-stat-card">
            <div className="vs-stat-top">
              <span className="vs-stat-icon vs-icon-blue">▤</span>
              <span className="vs-stat-label">ALL TICKETS</span>
            </div>
            <div className="vs-stat-number">{loading ? "—" : stats.total}</div>
            <p>Tickets submitted by you</p>
          </article>

          <article className="vs-stat-card">
            <div className="vs-stat-top">
              <span className="vs-stat-icon vs-icon-orange">◷</span>
              <span className="vs-stat-label">OPEN TICKETS</span>
            </div>
            <div className="vs-stat-number">{loading ? "—" : stats.open}</div>
            <p>Awaiting resolution</p>
          </article>

          <article className="vs-stat-card">
            <div className="vs-stat-top">
              <span className="vs-stat-icon vs-icon-green">✓</span>
              <span className="vs-stat-label">RESOLVED</span>
            </div>
            <div className="vs-stat-number">{loading ? "—" : stats.resolved}</div>
            <p>Resolved or closed tickets</p>
          </article>
        </section>

        <section className="vs-help-banner">
          <div className="vs-help-symbol">?</div>
          <div className="vs-help-copy">
            <h2>How can we help you today?</h2>
            <p>
              Describe your issue in a ticket and track the response
              from your support team.
            </p>
          </div>
          <button
            type="button"
            className="vs-secondary-btn"
            onClick={openCreatePage}
          >
            Get Support <span>→</span>
          </button>
        </section>

        <section className="vs-ticket-section">
          <div className="vs-section-heading">
            <div>
              <h2>My Support Tickets</h2>
              <p>View and track the tickets you have submitted.</p>
            </div>
            <button
              type="button"
              className="vs-refresh-btn"
              onClick={fetchTickets}
              disabled={loading}
            >
              <span>↻</span> Refresh
            </button>
          </div>

          <div className="vs-filters">
            <label className="vs-search">
              <span>⌕</span>
              <input
                type="search"
                placeholder="Search tickets..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>

            <select
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="All">All statuses</option>
              <option value="Open">Open</option>
              <option value="In Progress">In Progress</option>
              <option value="Resolved">Resolved</option>
              <option value="Closed">Closed</option>
            </select>

            <select
              aria-label="Filter by category"
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
            >
              <option value="All">All categories</option>
              {CATEGORIES.map((category) => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
          </div>

          <div className="vs-table-wrap">
            <table className="vs-table">
              <thead>
                <tr>
                  <th>Ticket Details</th>
                  <th>Category</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="vs-table-message">
                      Loading your tickets...
                    </td>
                  </tr>
                ) : filteredTickets.length === 0 ? (
                  <tr>
                    <td colSpan="6">
                      <div className="vs-empty">
                        <div className="vs-empty-icon">▤</div>
                        <h3>
                          {tickets.length === 0
                            ? "No support tickets yet"
                            : "No matching tickets found"}
                        </h3>
                        <p>
                          {tickets.length === 0
                            ? "Create a ticket when you need help."
                            : "Try changing your search or filters."}
                        </p>
                        {tickets.length === 0 && (
                          <button
                            className="vs-primary-btn"
                            type="button"
                            onClick={openCreatePage}
                          >
                            + Create Your First Ticket
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredTickets.map((ticket) => (
                    <tr key={ticket._id}>
                      <td>
                        <div className="vs-ticket-id">
                          {ticket.ticketNumber || "Support Ticket"}
                        </div>
                        <div className="vs-ticket-subject">{ticket.subject}</div>
                        <div className="vs-ticket-preview">{ticket.description}</div>
                      </td>
                      <td>
                        <span className="vs-category">{ticket.category}</span>
                      </td>
                      <td>
                        <span
                          className={`vs-priority vs-priority-${(
                            ticket.priority || "Medium"
                          ).toLowerCase()}`}
                        >
                          <span />
                          {ticket.priority || "Medium"}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`vs-status vs-status-${getStatusClass(
                            ticket.status
                          )}`}
                        >
                          {ticket.status || "Open"}
                        </span>
                      </td>
                      <td className="vs-date">{formatDate(ticket.createdAt)}</td>
                      <td>
                        <button
                          type="button"
                          className="vs-view-btn"
                          onClick={() => openTicketDetails(ticket)}
                        >
                          View Details →
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="vs-table-footer">
            Showing {loading ? 0 : filteredTickets.length} of{" "}
            {loading ? 0 : tickets.length} tickets
          </div>
        </section>
      </div>

      {/* Ticket details modal only; create form is a full page */}
      {selectedTicket && (
        <div
          className="vs-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedTicket(null);
            }
          }}
        >
          <section
            className="vs-modal vs-detail-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="vs-detail-title"
          >
            <div className="vs-modal-header">
              <div>
                <span className="vs-modal-kicker">
                  {selectedTicket.ticketNumber || "SUPPORT TICKET"}
                </span>
                <h2 id="vs-detail-title">{selectedTicket.subject}</h2>
                <p>Created {formatDate(selectedTicket.createdAt)}</p>
              </div>
              <button
                type="button"
                className="vs-close-btn"
                onClick={() => setSelectedTicket(null)}
                aria-label="Close details"
              >
                ×
              </button>
            </div>

            {detailLoading ? (
              <div className="vs-detail-loading">Loading ticket details...</div>
            ) : (
              <div className="vs-detail-content">
                <div className="vs-detail-meta">
                  <div>
                    <span>STATUS</span>
                    <strong>{selectedTicket.status || "Open"}</strong>
                  </div>
                  <div>
                    <span>CATEGORY</span>
                    <strong>{selectedTicket.category || "—"}</strong>
                  </div>
                  <div>
                    <span>PRIORITY</span>
                    <strong>{selectedTicket.priority || "Medium"}</strong>
                  </div>
                </div>

                <div className="vs-detail-block">
                  <h3>Your Message</h3>
                  <p>{selectedTicket.description}</p>
                </div>

                <div className="vs-reply-block">
                  <div className="vs-reply-avatar">S</div>
                  <div>
                    <h3>Support Team Response</h3>
                    <p>
                      {selectedTicket.adminReply?.trim()
                        ? selectedTicket.adminReply
                        : "No response from the support team yet."}
                    </p>
                    {selectedTicket.repliedAt && (
                      <span>Replied {formatDate(selectedTicket.repliedAt)}</span>
                    )}
                  </div>
                </div>

                <div className="vs-form-actions">
                  <button
                    type="button"
                    className="vs-primary-btn"
                    onClick={() => setSelectedTicket(null)}
                  >
                    Close Details
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      )}
    </main>
  );
}
